import Signal from 'ln/signal/Signal';
import Node from 'ln/node/Node';
import View from 'ln/view/View';
import Window from 'ln/node/Window';
import { ILink } from 'ln/list/ListRenderer';
import Model from 'ln/model/Model';
import { scanner } from 'ln/view/Scanner';

/**
 * Interface for all listener function that listen on the top signal
 */
interface ScrollChangeListener {
	( visible:Array<ILink<Model>>, ...args:any[] );
}

/**
 * A class that monitors the visible elements of a lernbuch
 */
class SequenceScrollMonitor extends View {
	
	// Offest-Top and Offset-Bottom (in case of fixed header / footer)
	public offsetTop:number = 0;
	public offsetBottom:number = 0;
	
	private visibleElements:Array<ILink<Model>> = [];
	private _elements:Array<ILink<Model>> = [];
	
	// event when the top visible element has changed
	public change:Signal<ScrollChangeListener> = new Signal<ScrollChangeListener>();
	
	constructor( elements:Array<ILink<Model>> = [] ) {
        
        super( {} );

		this._elements = elements;

		// register on window events
		Window.resize.add( this.update, this );
		Window.scroll.add( this.update, this );

	}

	/**
	 * Set the elements for the scrollMonitor
	 */
	set elements( elements:Array<ILink<Model>> ){
		this._elements = elements;
		this.visibleElements = [];
		this.update();
	}
	
	/**
	 * Iterates over all the rendered Elements and updates the visibleElements array
	 */
	public update() {
		
		var scrollInfo = Window.scrollInfo();
		var viewport = Window.viewport();
		
		var viewportTop:number = scrollInfo.top + this.offsetTop;
		var viewportHeight:number = viewport.height - (this.offsetTop + this.offsetBottom);
		var viewportBottom:number = viewportTop + viewportHeight;
		
		var currentElements:Array<ILink<Model>> = [];
		
		// loop over the rendered elements
		this._elements.forEach( ( element ) => {
			
			var bounds = element.node.bounds();
			
			// element larger than viewPort
			if (bounds.top < viewportTop && bounds.bottom > viewportBottom) {
				currentElements.push( element );
				return;
			}
			
			// element fully inside viewport
			if( bounds.top >= viewportTop && bounds.bottom <= viewportBottom) {
				currentElements.push( element );
				return;
			}
								
			// partial visible, calc overlappping height,
			// if more than 50% of viewport is covered -> add
			var visibleTop = Math.max( bounds.top, viewportTop );
			var visibleBot = Math.min( bounds.bottom, viewportBottom );
			if( ( visibleBot - visibleTop ) / viewportHeight >= 0.5 ) {
				currentElements.push( element );
				return;
			}
			
		});
            
		// compare 
		this.compareElements( currentElements );
	}
	
	/**
	 * Compares the given current elements and check with the visibleElements which are still visible or not.
     */
	private compareElements( currentElements:Array<ILink<Model>> ) {
		
		var hasChange = false;
		
		// check if current element is new visible
		currentElements.forEach( ( element, index )=> {
			if( this.visibleElements.indexOf(element) == -1 ) {
				hasChange = true;
			}
		});
		
		// check if any of the old elements are not visible anymore.
		this.visibleElements.forEach( ( element, index )=> {
			if( currentElements.indexOf(element) == -1 ) {
				hasChange = true;
			}
		});
		
		// check if top element has changed
		hasChange = hasChange || currentElements[0] != this.visibleElements[0];
		
		// update the visible element
		this.visibleElements = currentElements;
			
		if( hasChange ) this.change.dispatch( currentElements );
	}

}

export default SequenceScrollMonitor;


scanner.ioc.add( 'sequence-scroll-monitor', function( node ) {
	return new SequenceScrollMonitor( node.data.top ).render( node );
});